<template>
	<div
		ref="listRef"
		class="relative flex items-center w-full md:w-[calc(20%-20px)] min-h-[56px] pl-0 sm:pl-4 md:pl-6 pr-0 sm:pr-4 pb-2 md:pb-0 md:mt-0 mt-3 border-b-[1px] border-gray-light md:border-0"
	>
		<div @click="showList = !showList" class="flex-col cursor-pointer w-[calc(100%-30px)] pr-2">
			<span class="pb-1 text-dj-gray-dark text-sm font-medium">
				{{ $t("companies.searchbar.location.title") }}
			</span>
			<input
				v-if="canType || selectedLocations.length == 0"
				ref="inputRef"
				v-model="inputVmodel"
				class="w-full outline-none border-none bg-transparent max-h-6"
				type="text"
				:placeholder="placeholder"
			/>
			<span
				v-else
				@click="canType = true"
				class="block overflow-hidden overflow-ellipsis whitespace-nowrap w-[calc(100%-6px)] cursor-default"
			>
				{{
					selectedLocations
						.map((l) => l.name)
						.map((n) => n[locale])
						.join(", ")
				}}
			</span>
		</div>

		<DropdownButton v-model="showList" class="mt-2" />

		<div
			v-show="showList"
			class="absolute p-5 left-0 md:-left-8 top-[60px] md:top-[85px] w-[400px] max-w-[100%] md:max-w-[100vw] max-h-[350px] bg-white border border-dj-gray-light rounded-big flex flex-col z-30"
		>
			<client-only>
				<h3 class="font-medium mb-[10px]">{{ $t("mainSearchbar.location.results") }}</h3>
				<div class="flex flex-col gap-y-[10px] overflow-auto">
					<div v-if="false && (getCurrentRegionLocation.country.showCountry || inputVmodel.length < 1)">
						{{ getCurrentRegionLocation.country.name[locale] }}
					</div>
					<template v-for="(location, index) in getCurrentRegionLocation.list" :key="`region-${index}`">
						<BaseCheckbox
							v-show="location.showLocation || inputVmodel.length < 1"
							v-model="location.value"
							@change="onChange($event, location)"
						>
							{{ location.name[locale] }}
						</BaseCheckbox>
					</template>

					<template v-for="(country, index) in getOtherLocations" :key="`other-${index}`">
						<div v-if="false && (country.showCountry || inputVmodel.length < 1)">
							{{ country.name[locale] }}
						</div>
						<template v-for="(location, ind) in country.children" :key="`other-${index}-${ind}`">
							<BaseCheckbox
								v-show="location.showLocation || inputVmodel.length < 1"
								v-model="location.value"
								@change="onChange($event, location)"
							>
								{{ location.name[locale] }}
							</BaseCheckbox>
						</template>
					</template>
				</div>
			</client-only>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, type PropType, computed, onMounted } from "vue";
import { onClickOutside, useFocus } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { type locationObject } from "@@/types";

export default defineComponent({
	name: "SearchbarCompaniesLocation",
	props: {
		modelValue: {
			type: Array as PropType<string[]>,
			required: true,
		},
		filterList: {
			type: Array as PropType<string[]>,
			default: [],
		},
		placeholder: {
			type: String,
			default: "",
		},
	},
	emits: ["update:modelValue"],
	async setup(props, { emit }) {
		const { locale } = useI18n();
		const region = ref("");

		const inputRef = ref(null);
		const listRef = ref(null);

		const inputVmodel = ref("");

		const locationsData = ref([]);
		const locationsWithValue = ref([]);
		const selectedLocations = ref<locationObject[]>([]);

		const showList = ref(false);
		const canType = ref(false);

		if (props.filterList.length > 0) {
			locationsData.value = JSON.parse(JSON.stringify(props.filterList));
			setDataArrays();
		}

		function setDataArrays() {
			locationsWithValue.value = transformArray();
		}

		function transformArray() {
			return sortArrayByObjectByLocale(locationsData.value, "name", locale.value).value.map((location) => {
				return {
					...location,
					value: false,
				};
			});
		}

		const getCurrentRegionLocation = computed(() => {
			const country = locationsWithValue.value.filter((country) => country.slug == region.value)[0];
			const list = sortArrayByObjectByLocale(country.children, "name", locale.value).value;

			return { list: list, country: country };
		});

		const getOtherLocations = computed(() => {
			const otherLocations = locationsWithValue.value.filter((country) => country.slug != region.value);

			otherLocations.forEach((el, index) => {
				otherLocations[index].children = sortArrayByObjectByLocale(el.children, "name", locale.value).value;
			});

			return otherLocations;
		});

		watch(inputVmodel, () => {
			if (inputVmodel.value.length > 0) {
				const pattern = new RegExp(inputVmodel.value, "gi");
				locationsWithValue.value.forEach((country, index) => {
					let showThisCountry = false;
					country.children.forEach((location, ind) => {
						if (location.name[locale.value].match(pattern)) {
							locationsWithValue.value[index].children[ind].showLocation = true;
							showThisCountry = true;
						} else {
							locationsWithValue.value[index].children[ind].showLocation = false;
						}
					});

					locationsWithValue.value[index].showCountry = showThisCountry;
				});

				showList.value = true;
			} else {
				locationsWithValue.value.forEach((country, index) => {
					country.children.forEach((location, ind) => {
						locationsWithValue.value[index].children[ind].showLocation = true;
					});

					locationsWithValue.value[index].showCountry = true;
				});
			}
		});

		const { focused: inputFocus } = useFocus(inputRef);

		onClickOutside(listRef, () => {
			showList.value = false;
			inputVmodel.value = "";
		});

		onClickOutside(inputRef, () => {
			canType.value = false;
		});

		onMounted(() => {
			region.value = useRegion.getCurrentRegion().code == "HU" ? "hungary" : "romania";
			// get locations ID from url & set selectedLocation
			const query = useRoute().query;
			if (query.location) {
				const queryLocation = query.location.toString().split(",");
				queryLocation.forEach((ql) => {
					const num = Number.parseInt(ql);

					locationsWithValue.value.forEach((country, index) => {
						const targetIndex = locationsWithValue.value[index].children.findIndex(
							(pred) => pred.id == num
						);
						if (targetIndex > -1) {
							locationsWithValue.value[index].children[targetIndex].value = true;
							selectedLocations.value.push(locationsWithValue.value[index].children[targetIndex]);
						}
					});
				});
				emit("update:modelValue", selectedLocations.value);
			}
		});

		function onChange(event, settlement: locationObject) {
			event
				? selectedLocations.value.push(settlement)
				: selectedLocations.value.splice(selectedLocations.value.indexOf(settlement), 1);
			emit("update:modelValue", selectedLocations.value);
		}

		return {
			inputRef,
			inputVmodel,
			inputFocus,
			listRef,
			showList,
			onChange,
			selectedLocations,
			canType,
			locale,
			region,
			getCurrentRegionLocation,
			getOtherLocations,
			sortArrayByObjectByLocale,
		};
	},
});
</script>
